/**
 * Where recorded audio actually lives.
 *
 * The course data inside the .pptx holds roughly 1 MB in total (SPIKE 2), and
 * one minute of Opus is about 180 KB, so audio cannot go in the deck. It is
 * kept in IndexedDB on the add-in's own origin, keyed by the SHA-256 of the
 * bytes, mirrored to the loopback SlideForge service, and written into the
 * package at publish time.
 *
 * A companion bundle is still required when the deck moves to another local
 * installation; a machine-local service cannot follow an emailed file.
 */

import { createLocalSidecarClient } from '../sidecar/client.js';
import { sha256Hex } from './hash.js';

const DB_NAME = 'slideforge-audio';
const DB_VERSION = 1;
const STORE = 'clips';

export interface StoredClip {
  readonly audioId: string;
  readonly mimeType: string;
  readonly byteLength: number;
  readonly blob: Blob;
}

export function audioStoreAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'audioId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB could not be opened.'));
  });
}

function run<T>(mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = action(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('The audio store rejected the operation.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

/**
 * SHA-256 of the bytes, hex encoded.
 *
 * Content addressing means an identical re-record costs no extra storage, and a
 * take can be moved between slides without copying anything.
 */
export async function hashAudio(bytes: ArrayBuffer): Promise<string> {
  return sha256Hex(bytes);
}

export async function putClip(blob: Blob): Promise<StoredClip> {
  const bytes = await blob.arrayBuffer();
  const audioId = await hashAudio(bytes);
  const clip: StoredClip = {
    audioId,
    mimeType: blob.type || 'audio/webm',
    byteLength: blob.size,
    blob,
  };
  await run('readwrite', (store) => store.put(clip));
  const sidecar = createLocalSidecarClient();
  if (sidecar) await sidecar.putAsset(clip).catch(() => undefined);
  return clip;
}

export async function getClip(audioId: string): Promise<StoredClip | undefined> {
  if (audioStoreAvailable()) {
    const result = await run<StoredClip | undefined>('readonly', (store) => store.get(audioId));
    if (result) return result;
  }
  const sidecar = createLocalSidecarClient();
  const recovered = await sidecar?.getAsset(audioId).catch(() => undefined);
  if (!recovered) return undefined;
  if (audioStoreAvailable()) await run('readwrite', (store) => store.put(recovered));
  return recovered;
}

export async function deleteClip(audioId: string): Promise<void> {
  await run('readwrite', (store) => store.delete(audioId));
}

export async function listClipIds(): Promise<string[]> {
  const keys = await run<IDBValidKey[]>('readonly', (store) => store.getAllKeys());
  return keys.map(String);
}

/**
 * Drops clips nothing references any more.
 *
 * Deleting a take must not delete its bytes immediately: two takes can share an
 * audioId, and undo is a reasonable thing to want. Collection is explicit and
 * happens against the full set of live ids.
 */
export async function collectGarbage(liveAudioIds: ReadonlySet<string>): Promise<number> {
  const all = await listClipIds();
  let removed = 0;
  for (const id of all) {
    if (liveAudioIds.has(id)) continue;
    await deleteClip(id);
    removed += 1;
  }
  return removed;
}

/** Total bytes held locally, for the "this is on your machine" disclosure. */
export async function usedBytes(): Promise<number> {
  const clips = await run<StoredClip[]>('readonly', (store) => store.getAll());
  return clips.reduce((sum, clip) => sum + clip.byteLength, 0);
}
